import { useSuspenseQuery } from "@apollo/client";
import { Edit, Mail, Phone, User, Calendar, Clock, Shield } from "lucide-react";
import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { AvatarUpload } from "./AvatarUpload";
import { UserProfileEditForm } from "./UserProfileEditForm";
import { ErrorCard } from "@/components/ErrorCard";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { graphql } from "@/graphql";
import { toDisplayDate } from "@/lib/utils/dates";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatName, formatPhoneNumber } from "@/lib/utils/formatting";

export function UserProfile() {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditSuccess = () => {
    setIsEditing(false);
  };

  return (
    <>
      <PageHeader>
        <PageHeader.Content>
          <PageHeader.Title>Profile</PageHeader.Title>
          <PageHeader.Description>Manage your personal information and account settings</PageHeader.Description>
        </PageHeader.Content>
        <PageHeader.Actions>
          <Button onClick={() => setIsEditing(true)} className="gap-2">
            <Edit className="h-4 w-4" />
            Edit Profile
          </Button>
        </PageHeader.Actions>
      </PageHeader>
      <div className="p-6 space-y-6">
        <div className="grid gap-6 md:grid-cols-3">
          {/* Profile Overview */}
          <ErrorBoundary
            FallbackComponent={({ error }) => (
              <div className="md:col-span-1">
                <ErrorCard title="Failed to load profile" description={getErrorMessage(error)} />
              </div>
            )}
          >
            <Suspense fallback={<ProfileOverviewSkeleton />}>
              <ProfileOverviewCard onEditSuccess={handleEditSuccess} />
            </Suspense>
          </ErrorBoundary>

          {/* Personal Information */}
          <ErrorBoundary
            FallbackComponent={({ error }) => (
              <div className="md:col-span-2">
                <ErrorCard title="Failed to load personal information" description={getErrorMessage(error)} />
              </div>
            )}
          >
            <Suspense fallback={<PersonalInformationSkeleton />}>
              <PersonalInformationCard />
            </Suspense>
          </ErrorBoundary>

          {/* Account Information */}
          <ErrorBoundary
            FallbackComponent={({ error }) => (
              <div className="md:col-span-3">
                <ErrorCard title="Failed to load account information" description={getErrorMessage(error)} />
              </div>
            )}
          >
            <Suspense fallback={<AccountInformationSkeleton />}>
              <AccountInformationCard />
            </Suspense>
          </ErrorBoundary>
        </div>

        {/* Edit Form Modal */}
        <UserProfileEditForm open={isEditing} onOpenChange={setIsEditing} onSuccess={handleEditSuccess} />
      </div>
    </>
  );
}

function ProfileOverviewCard({ onEditSuccess }: { onEditSuccess: () => void }) {
  const { data } = useSuspenseQuery(userProfileOverviewQuery);
  const user = data.currentUser;

  if (!user) {
    return (
      <Card className="md:col-span-1 min-h-[280px] flex items-center justify-center">
        <p className="text-muted-foreground">No profile data available.</p>
      </Card>
    );
  }

  return (
    <Card className="md:col-span-1 min-h-[280px]">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <AvatarUpload user={user} onSuccess={onEditSuccess} />
        </div>
        <CardTitle className="text-xl">{formatName({ firstName: user.firstName, lastName: user.lastName })}</CardTitle>
        <CardDescription className="space-y-1">
          <div className="flex items-center justify-center gap-2">
            <User className="h-4 w-4" />
            {user.jobTitle || "No job title"}
          </div>
          {user.isStaff ? (
            <Badge variant="default" className="text-xs">
              <Shield className="h-3 w-3 mr-1" />
              Administrator
            </Badge>
          ) : null}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

function PersonalInformationCard() {
  const { data } = useSuspenseQuery(userPersonalInfoQuery);
  const user = data.currentUser;

  if (!user) {
    return (
      <Card className="md:col-span-2 min-h-[280px] flex items-center justify-center">
        <p className="text-muted-foreground">No profile data available.</p>
      </Card>
    );
  }

  return (
    <Card className="md:col-span-2 min-h-[280px]">
      <CardHeader>
        <CardTitle>Personal Information</CardTitle>
        <CardDescription>Your personal details and contact information</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Mail className="h-4 w-4" />
              Email
            </div>
            <p className="text-sm text-muted-foreground">{user.email || "No email provided"}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Phone className="h-4 w-4" />
              Phone
            </div>
            <p className="text-sm text-muted-foreground">
              {formatPhoneNumber(user.phone) || "No phone number provided"}
            </p>
          </div>
        </div>

        {user.about ? (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="text-sm font-medium">About</div>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{user.about}</p>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AccountInformationCard() {
  const { data } = useSuspenseQuery(userAccountInfoQuery);
  const user = data.currentUser;

  if (!user) {
    return (
      <Card className="md:col-span-3 min-h-[200px] flex items-center justify-center">
        <p className="text-muted-foreground">No profile data available.</p>
      </Card>
    );
  }

  return (
    <Card className="md:col-span-3 min-h-[200px]">
      <CardHeader>
        <CardTitle>Account Information</CardTitle>
        <CardDescription>Your account status and activity</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="h-4 w-4" />
              Member Since
            </div>
            <p className="text-sm text-muted-foreground">
              {user.dateJoined ? toDisplayDate(user.dateJoined, "MMM d, yyyy") || "\u2014" : "\u2014"}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4" />
              Last Login
            </div>
            <p className="text-sm text-muted-foreground">
              {user.lastLogin ? toDisplayDate(user.lastLogin, "MMM d, yyyy 'at' h:mm a") || "Never" : "Never"}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Shield className="h-4 w-4" />
              Account Status
            </div>
            <div className="flex gap-2">
              <Badge variant={user.isStaff ? "default" : "secondary"}>{user.isStaff ? "Administrator" : "User"}</Badge>
              {user.isTermsAccepted && <Badge variant="outline">Terms Accepted</Badge>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Skeleton Components
function ProfileOverviewSkeleton() {
  return (
    <Card className="md:col-span-1 min-h-[280px]">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <Skeleton className="h-32 w-32 rounded-full" />
        </div>
        <Skeleton className="h-6 w-32 mx-auto mb-2" />
        <div className="space-y-1">
          <Skeleton className="h-4 w-24 mx-auto" />
          <Skeleton className="h-5 w-20 mx-auto" />
        </div>
      </CardHeader>
    </Card>
  );
}

function PersonalInformationSkeleton() {
  return (
    <Card className="md:col-span-2 min-h-[280px]">
      <CardHeader>
        <Skeleton className="h-6 w-40 mb-2" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountInformationSkeleton() {
  return (
    <Card className="md:col-span-3 min-h-[200px]">
      <CardHeader>
        <Skeleton className="h-6 w-40 mb-2" />
        <Skeleton className="h-4 w-48" />
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <div className="flex gap-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-24" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// GraphQL Queries
export const userProfileOverviewQuery = graphql(`
  query UserProfileOverview {
    currentUser {
      id
      firstName
      lastName
      jobTitle
      isStaff
      image
      about
      phone
      imageThumb {
        imageSquare32x32
        imageSquare64x64
        imageSquare96x96
        imageSquare128x128
      }
    }
  }
`);

const userPersonalInfoQuery = graphql(`
  query UserPersonalInfo {
    currentUser {
      id
      email
      phone
      about
    }
  }
`);

const userAccountInfoQuery = graphql(`
  query UserAccountInfo {
    currentUser {
      id
      isStaff
      isTermsAccepted
      dateJoined
      lastLogin
    }
  }
`);
